import { Star } from '@phosphor-icons/react'
import { ChevronLeft, ChevronRight, Search, X } from '@/design-system/icons'
import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn, CountBadge, Icon } from '@/design-system'
import { useT } from '@/i18n'
import { useNavBadges } from '@/navigation/badges'
import { hasNewPage, isNewPage } from '@/navigation/newBadge'
import type { NavGroup, NavNode, NavPage } from '@/navigation/types'
import { isPageActive, searchNav, useActiveNav, useVisibleNav } from '@/navigation/useNavigation'
import { pageIcon } from '@/navigation/pageIcons'
import { useUi } from '@/stores/ui'
import { useTabs } from '@/stores/tabs'
import { AliensMark } from '@/design-system'
import { preloadPath } from '@/app/pageRegistry'
import { tabMeta, useTabLink } from './workspace/tabMeta'
import { useQuery } from '@tanstack/react-query'
import { authService } from '@/services/authService'
import { useAuth } from '@/stores/auth'
import { pick } from '@/domain/extract'

/**
 * The phone menu, the way phone apps do it (owner, 2026-10-17: "ديزاين موبايل حاسه مش متظبط وكذلك سايد بار … اعمل الصح"):
 * one level on the screen at a time — a section opens on its own screen with a back row — instead of a tree three
 * levels deep in a narrow drawer, where long Arabic names were cut off. Rows are 48 px for a thumb, the text 15 px,
 * the search is the first thing, and the pages last opened are one tap away.
 */
export function MobileNav() {
  const { t, l, locale } = useT()
  const ar = locale === 'ar'
  const tree = useVisibleNav()
  const active = useActiveNav()
  const setMobileNav = useUi((s) => s.setMobileNav)
  const setMenuOrigin = useUi((s) => s.setMenuOrigin)
  // the menu opens on all the sections; only the back arrow of a page opened from it reopens that page's section
  // (owner, 2026-10-19: the menu on «صفحتي» showed only the home section's pages)
  const backToSection = useUi((s) => s.menuBack)
  const setMenuBack = useUi((s) => s.setMenuBack)
  const tabLink = useTabLink()
  const [query, setQuery] = useState('')
  // the open path: section, then sub-sections; empty = the list of sections. It opens on the section of the page
  // being shown, so going back lands where you came from (owner, 2026-10-19: «ما اعمل عوده من فوق بيطلعني ع سايد بار
  // خالص مش القسم ال دخلت منه»)
  const [stack, setStack] = useState<NavGroup[]>(() => (backToSection && active ? active.trail : []))
  const close = () => { setMobileNav(false); setMenuBack(false) }
  const results = useMemo(() => searchNav(tree, query), [tree, query])
  const current = stack.at(-1)
  const tabs = useTabs((s) => s.tabs)
  const recent = useMemo(() => [...tabs].filter((x) => x.url !== '/').sort((a, b) => b.lastUsed - a.lastUsed).slice(0, 5).map((x) => ({ url: x.url, meta: tabMeta(x.url, l) })), [tabs, l])
  const favorites = useUi((s) => s.favorites)
  const favPages = useMemo(() => { const byId = new Map(allPages(tree).map((p) => [p.id, p])); return favorites.map((id) => byId.get(id)).filter((p): p is NavPage => Boolean(p)) }, [tree, favorites])
  const Back = ar ? ChevronRight : ChevronLeft
  const Forward = ar ? ChevronLeft : ChevronRight
  // the branch under the COREX name, from the list the top bar already holds (same query key)
  const user = useAuth((s) => s.user)
  const branches = useQuery({ queryKey: ['auth', 'branches'], queryFn: authService.branches, staleTime: 10 * 60_000, enabled: Boolean(user) })
  const branchName = (() => {
    const b = branches.data?.find((x) => Number(pick(x, 'branchID', 'branchId', 'id')) === user?.branchId)
    return b ? String(pick(b, 'branchName', 'name', 'branchNameAr', 'branchNameEn') ?? '') : null
  })()

  return (
    <nav aria-label="Main" className="flex h-full flex-col bg-sidebar pt-[env(safe-area-inset-top)] text-sidebar-fg">
      {/*
        * The menu's head (owner, 2026-10-31: «ديزاين موبايل من فوق عند فتحته سايد بار وحش اوي وبايظ»): the COREX tile
        * with the company and the branch, or, inside a section, back and its name; close is a soft square like the top
        * bar's buttons. No hairline: the search below sets it apart.
        */}
      <div className="flex shrink-0 items-center gap-3 px-4 pb-1 pt-4">
        {current ? (
          <>
            <button type="button" onClick={() => setStack(stack.slice(0, -1))} className="flex size-11 shrink-0 items-center justify-center rounded-[14px] bg-bg-subtle text-sidebar-fg-strong" aria-label={ar ? 'رجوع' : 'Back'}>
              <Back size={20} aria-hidden />
            </button>
            <span className="min-w-0 flex-1 truncate text-[17px] font-extrabold text-sidebar-fg-strong">{l(current.label)}</span>
          </>
        ) : (
          <Link to="/" {...tabLink('/', close)} className="flex min-w-0 flex-1 items-center gap-3 text-sidebar-fg-strong">
            <span className="brand-tile relative flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-[14px]">
              <AliensMark tone="white" className="relative z-[1] h-[20px] w-auto" title="COREX" />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-[17px] font-extrabold tracking-[-0.2px]">COREX</span>
              <span className="block truncate text-xs font-semibold text-sidebar-fg">{branchName ? `${ar ? 'فرع' : 'Branch'} ${branchName}` : ar ? 'القائمة الرئيسية' : 'Main menu'}</span>
            </span>
          </Link>
        )}
        <button type="button" onClick={close} className="flex size-11 shrink-0 items-center justify-center rounded-[14px] bg-bg-subtle text-sidebar-fg-strong" aria-label={t('common.close')}>
          <X size={20} aria-hidden />
        </button>
      </div>

      <div className="shrink-0 px-3 pb-2 pt-3">
        <div className="relative flex items-center">
          <Search size={18} className="pointer-events-none absolute start-3 text-sidebar-fg" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('shell.searchNav')}
            aria-label={t('shell.searchNav')}
            className="h-11 w-full rounded-[10px] border border-sidebar-border bg-bg-subtle pe-10 ps-10 text-[15px] text-sidebar-fg-strong placeholder:text-sidebar-fg/70 focus:border-brand focus:bg-surface focus:outline-none"
          />
          {query && <button type="button" onClick={() => setQuery('')} className="absolute end-2 rounded-md p-1.5 text-sidebar-fg" aria-label={t('common.clear')}><X size={16} aria-hidden /></button>}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {query ? (
          <ul className="space-y-1">
            {results.length === 0 && <li className="px-3 py-8 text-center text-sm">{t('shell.noNavResults')}</li>}
            {results.map((r) => (
              <li key={r.page.id}>
                <Link to={r.page.path} {...tabLink(r.page.path, () => (setQuery(''), close()))} className="flex items-center gap-2 rounded-xl px-3 py-2.5 hover:bg-sidebar-hover">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold leading-snug text-sidebar-fg-strong">{l(r.page.label)}</span>
                    <span className="mt-0.5 block text-xs leading-snug">{r.trail.map((g) => l(g.label)).join(' › ')}</span>
                  </span>
                  {/* المفضلة from the search too (owner, 2026-10-23: «لما اعمل سيرش ع حاجه مش بيجي جنبها علامه») */}
                  <FavStar id={r.page.id} />
                </Link>
              </li>
            ))}
          </ul>
        ) : current ? (
          <ul className="space-y-1">
            {current.children.map((n) => <Row key={n.id} node={n} onOpen={(g) => setStack([...stack, g])} onNavigate={(path) => { setMenuOrigin(path ?? null); close() }} Forward={Forward} activeTrail={active?.trail.map((g) => g.id) ?? []} />)}
          </ul>
        ) : (
          <>
            {recent.length > 0 && (
              <section className="mb-2">
                <p className="px-3 pb-1 pt-1 text-xs font-semibold text-sidebar-fg">{ar ? 'آخر الصفحات' : 'Recent'}</p>
                <ul className="flex gap-2 overflow-x-auto px-1 pb-2">
                  {recent.map((r) => (
                    <li key={r.url} className="shrink-0">
                      <Link to={r.url} {...tabLink(r.url, close)} className="flex max-w-[11rem] items-center gap-2 rounded-xl border border-sidebar-border bg-surface px-3 py-2">
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: r.meta.hue }}><r.meta.icon size={15} weight="fill" aria-hidden /></span>
                        <span className="min-w-0 truncate text-sm font-semibold text-sidebar-fg-strong">{r.meta.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {/* المفضلة: the pages this user starred, on top as in the sidebar */}
            {favPages.length > 0 && (
              <section className="mb-2">
                <p className="flex items-center gap-1.5 px-3 pb-1 pt-1 text-xs font-semibold text-sidebar-fg"><Star size={12} weight="fill" className="text-amber-500" aria-hidden />{ar ? 'المفضلة' : 'Favorites'}</p>
                <ul className="space-y-1">
                  {favPages.map((p) => <Row key={p.id} node={p} onOpen={() => undefined} onNavigate={(path) => { setMenuOrigin(path ?? null); close() }} Forward={Forward} activeTrail={[]} />)}
                </ul>
              </section>
            )}
            <ul className="space-y-1">
              {tree.map((section) => <Row key={section.id} node={section} top onOpen={(g) => setStack([g])} onNavigate={(path) => { setMenuOrigin(path ?? null); close() }} Forward={Forward} activeTrail={active?.trail.map((g) => g.id) ?? []} />)}
            </ul>
          </>
        )}
      </div>
    </nav>
  )
}

/** One row: a section or sub-section opens its own screen; a page goes there. */
function Row({ node, top, onOpen, onNavigate, Forward, activeTrail }: { node: NavNode; top?: boolean; onOpen: (g: NavGroup) => void; onNavigate: (path?: string) => void; Forward: typeof ChevronLeft; activeTrail: string[] }) {
  const { l } = useT()
  const badges = useNavBadges()
  const { pathname, search } = useLocation()
  const tabLink = useTabLink()
  if (node.kind === 'page') return <PageRow page={node} active={isPageActive(node, pathname, search)} onNavigate={onNavigate} tabLink={tabLink} badge={node.badge ? badges[node.badge] : undefined} />
  const isActive = activeTrail.includes(node.id)
  const total = countBadges(node, badges)
  return (
    <li>
      {/* the portal's rows (owner, 2026-10-20): plain grey icon, the open section soft red with a red icon and label */}
      <button type="button" onClick={() => onOpen(node)} className={cn('flex min-h-12 w-full items-center gap-3 rounded-[10px] px-3 py-2 text-start', isActive ? 'bg-sidebar-active' : 'hover:bg-sidebar-hover')}>
        {top && node.icon && <span className={cn('flex shrink-0', isActive ? 'text-sidebar-active-fg' : 'text-sidebar-icon')}><Icon icon={node.icon} size={22} weight={isActive ? 'fill' : 'duotone'} /></span>}
        <span className={cn('min-w-0 flex-1 text-[15px] leading-snug', isActive ? 'font-bold text-sidebar-active-fg' : 'font-semibold text-sidebar-fg-strong')}>{l(node.label)}</span>
        {hasNewPage(node) && <NewBadge />}
        <CountBadge value={total} />
        <Forward size={18} className="shrink-0 opacity-60" aria-hidden />
      </button>
    </li>
  )
}

function PageRow({ page, active, onNavigate, tabLink, badge }: { page: NavPage; active: boolean; onNavigate: (path?: string) => void; tabLink: ReturnType<typeof useTabLink>; badge: number | undefined }) {
  const { l } = useT()
  const PIcon = pageIcon(page.id)
  return (
    <li>
      <Link to={page.path} {...tabLink(page.path, () => onNavigate(page.path))} onPointerDown={() => preloadPath(page.path)} aria-current={active ? 'page' : undefined}
        className={cn('flex min-h-12 items-center gap-3 rounded-[10px] px-3 py-2', active ? 'bg-sidebar-active' : 'hover:bg-sidebar-hover')}>
        {PIcon && <span className={cn('flex shrink-0', active ? 'text-sidebar-active-fg' : 'text-sidebar-icon')}><PIcon size={20} weight={active ? 'fill' : 'duotone'} aria-hidden /></span>}
        <span className={cn('min-w-0 flex-1 text-[15px] leading-snug', active ? 'font-bold text-sidebar-active-fg' : 'font-medium text-sidebar-fg-strong')}>{l(page.label)}</span>
        {isNewPage(page) && <NewBadge />}
        {page.badge && <CountBadge value={badge} />}
        {/* المفضلة on the phone too (owner, 2026-10-23: «ايقونه المفضله مش ظاهره ف ديزاين موبايل») */}
        <FavStar id={page.id} />
      </Link>
    </li>
  )
}

/** The star of one page: always there on the phone, a thumb's size, faint until starred. */
function FavStar({ id }: { id: string }) {
  const fav = useUi((s) => s.favorites.includes(id))
  const toggleFavorite = useUi((s) => s.toggleFavorite)
  return (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(id) }}
      className={cn('-me-1 flex size-9 shrink-0 items-center justify-center rounded-lg', fav ? 'text-amber-500' : 'text-sidebar-icon opacity-60')}
      aria-label={fav ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
      title={fav ? 'إزالة من المفضلة' : 'إضافة إلى المفضلة'}
    >
      <Star size={18} weight={fav ? 'fill' : 'regular'} aria-hidden />
    </button>
  )
}

/** Every page of the menu, at any depth — the starred ones are looked up here. */
function allPages(nodes: NavNode[]): NavPage[] {
  return nodes.flatMap((n) => (n.kind === 'page' ? [n] : allPages(n.children)))
}

/** A new page's first week: a small red dot, as in the sidebar (owner, 2026-10-20). */
function NewBadge() {
  return <span className="size-2 shrink-0 rounded-full bg-danger" title="جديد" aria-label="جديد" />
}

function countBadges(node: NavNode, badges: Record<string, number | undefined>): number {
  if (node.kind === 'page') return node.badge ? (badges[node.badge] ?? 0) : 0
  const keys = new Set<string>()
  const walk = (n: NavNode) => (n.kind === 'page' ? n.badge && keys.add(n.badge) : n.children.forEach(walk))
  walk(node)
  return [...keys].reduce((s, k) => s + (badges[k] ?? 0), 0)
}
